import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import {
  formatMealDateTime,
  getMealEventLabel,
  getMealRuleSourceLabel,
  getMealStatusLabel,
  getMealTypeLabel,
  humanizeMealCode,
} from "../mealIncidenciasUtils";
import { MealConditionChip, MealPriorityChip, MealStatusChip } from "./MealIncidentTable";
import MealIncidentActions from "./MealIncidentActions";
import MealReviewData from "./MealReviewData";

const DetailSection = ({ title, icon, children }) => (
  <Box component="section" py={2.25}>
    <Typography variant="h6" fontWeight={900} display="flex" alignItems="center" gap={1} mb={1.5}>
      {icon}
      {title}
    </Typography>
    {children}
  </Box>
);

const MealIncidentDetail = ({
  open,
  loading,
  error,
  incident,
  history,
  currentUser,
  loadingAction,
  onClose,
  onRetry,
  onAction,
}) => {
  const records = Array.isArray(incident?.records) ? incident.records : [];
  const events = Array.isArray(history) && history.length > 0
    ? history
    : Array.isArray(incident?.history) ? incident.history : [];
  const rule = incident?.rule || {};

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 620, lg: 760 }, maxWidth: "100%" } }}
      aria-label="Detalle de incidencia"
    >
      <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "background.paper", borderBottom: 1, borderColor: "divider", p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Box minWidth={0}>
            <Typography variant="overline" color="primary" fontWeight={900}>MEAL</Typography>
            <Typography variant="h5" fontWeight={900} noWrap>
              {incident ? getMealTypeLabel(incident.type) : "Detalle de incidencia"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Cerrar detalle"><CloseOutlinedIcon /></IconButton>
        </Stack>
      </Box>

      <Box px={{ xs: 2, sm: 3 }} pb={4}>
        {loading && (
          <Box minHeight={260} display="flex" alignItems="center" justifyContent="center">
            <CircularProgress aria-label="Cargando detalle de incidencia" />
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error" action={<Button color="inherit" onClick={onRetry}>Reintentar</Button>} sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && incident && (
          <>
            <DetailSection title="Incidencia">
              <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
                <MealPriorityChip value={incident.priority} />
                <MealStatusChip value={incident.status} />
                <MealConditionChip value={incident.conditionActive} />
              </Stack>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }} gap={1.5}>
                <Box><Typography variant="caption" color="text.secondary">Identificador</Typography><Typography fontWeight={800}>#{incident.id}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Responsable</Typography><Typography fontWeight={800}>{incident.assignedToName || "Sin asignar"}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Detectada</Typography><Typography>{formatMealDateTime(incident.detectedAt)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Actualizada</Typography><Typography>{formatMealDateTime(incident.updatedAt)}</Typography></Box>
              </Box>
            </DetailSection>
            <Divider />

            <DetailSection title="Motivo de detección" icon={<GavelOutlinedIcon color="primary" fontSize="small" />}>
              <Typography variant="body1" mb={1.5}>{incident.description || incident.reason || getMealTypeLabel(incident.type)}</Typography>
              <Box sx={{ borderLeft: "4px solid #009fe3", pl: 2, py: 0.5 }}>
                <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                  <Typography fontWeight={900}>{rule.code ? humanizeMealCode(rule.code) : "Regla efectiva"}</Typography>
                  {typeof rule.enabled === "boolean" && (
                    <Chip
                      size="small"
                      label={rule.enabled ? "Regla habilitada" : "Regla deshabilitada"}
                      color={rule.enabled ? "success" : "default"}
                      variant="outlined"
                    />
                  )}
                </Stack>
                <Typography variant="body2" color="text.secondary">{getMealRuleSourceLabel(rule.source)}</Typography>
                {rule.severity && <Typography variant="body2">Severidad: {humanizeMealCode(rule.severity)}</Typography>}
                {Array.isArray(rule.relatedCodes) && rule.relatedCodes.length > 0 && (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} mt={1}>
                    {rule.relatedCodes.map((code) => <Chip key={code} size="small" label={humanizeMealCode(code)} variant="outlined" />)}
                  </Stack>
                )}
              </Box>
            </DetailSection>
            <Divider />

            <DetailSection title={`Registros vinculados (${records.length})`} icon={<LinkOutlinedIcon color="primary" fontSize="small" />}>
              {records.length === 0 ? (
                <Typography color="text.secondary">No hay registros vinculados disponibles.</Typography>
              ) : (
                <Stack spacing={2}>
                  {records.map((record, index) => (
                    <Box key={`${record.sourceFamily}-${record.recordId}-${index}`} sx={{ border: 1, borderColor: "divider", borderRadius: 2, p: 2 }}>
                      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1} mb={1.5}>
                        <Box>
                          <Typography fontWeight={900}>Registro #{record.recordId || index + 1}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Fuente: {humanizeMealCode(record.sourceFamily)} · Origen: {humanizeMealCode(record.source)}
                          </Typography>
                        </Box>
                        <Chip size="small" label={record.isCurrent ? "Versión actual" : "Versión histórica"} color={record.isCurrent ? "success" : "default"} variant="outlined" />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                        Versión observada: {record.firstVersionObserved ?? "—"} → {record.lastVersionObserved ?? "—"}
                      </Typography>
                      <MealReviewData data={record.reviewData} />
                    </Box>
                  ))}
                </Stack>
              )}
            </DetailSection>
            <Divider />

            <DetailSection title="Historial" icon={<HistoryOutlinedIcon color="primary" fontSize="small" />}>
              {events.length === 0 ? (
                <Typography color="text.secondary">No hay eventos disponibles.</Typography>
              ) : (
                <Stack spacing={0}>
                  {events.map((event, index) => (
                    <Box key={event.id || `${event.action || event.type}-${index}`} sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 2, pb: index === events.length - 1 ? 0 : 2, position: "relative" }}>
                      <Box sx={{ position: "absolute", width: 10, height: 10, borderRadius: "50%", bgcolor: "primary.main", left: -6, top: 5 }} />
                      <Typography fontWeight={900}>{getMealEventLabel(event.action || event.eventType || event.type)}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatMealDateTime(event.createdAt)}{event.actorName || event.actor?.name ? ` · ${event.actorName || event.actor.name}` : ""}</Typography>
                      {(event.previousStatus || event.fromState || event.nextStatus || event.toState) && (
                        <Typography variant="body2">{getMealStatusLabel(event.previousStatus || event.fromState)} → {getMealStatusLabel(event.nextStatus || event.toState)}</Typography>
                      )}
                      {(event.resolutionType || event.resolution) && <Typography variant="body2">Resolución: {humanizeMealCode(event.resolutionType || event.resolution)}</Typography>}
                      {event.comment && <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>{event.comment}</Typography>}
                    </Box>
                  ))}
                </Stack>
              )}
            </DetailSection>
            <Divider />

            <DetailSection title="Acciones disponibles">
              <MealIncidentActions
                incident={incident}
                currentUser={currentUser}
                loadingAction={loadingAction}
                onAction={onAction}
              />
            </DetailSection>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default MealIncidentDetail;
